import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { fmtErr } from "@/lib/api";
import { UserPlus, ShieldCheck } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "", confirm: "" });
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError(t("auth.mismatch"));
    if (!accept) return setError(t("auth.mustAccept"));
    setLoading(true);
    try {
      await register({
        full_name: form.full_name, email: form.email,
        phone: form.phone, password: form.password,
      });
      setDone(true);
      setTimeout(() => navigate("/dashboard"), 2200);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-1.5 bg-white border-[#E6EBF1] text-[#0A2540] focus-visible:ring-[#635BFF]/40";

  return (
    <div className="min-h-screen bg-[#F6F9FC] flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-5 pt-24 pb-16">
        <div className="bg-white border border-[#E6EBF1] rounded-2xl p-8 w-full max-w-md shadow-[0_20px_50px_rgba(10,37,64,0.08)]" data-testid="register-card">
          {done ? (
            <div className="text-center py-6" data-testid="register-success">
              <ShieldCheck size={40} className="text-emerald-500 mx-auto mb-4" />
              <h2 className="font-display text-xl font-bold text-[#0A2540]">{t("auth.pendingTitle")}</h2>
              <p className="text-sm text-[#425466] mt-3 leading-relaxed">{t("auth.pendingBody")}</p>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 rounded-xl bg-[#635BFF]/10 border border-[#635BFF]/20 flex items-center justify-center mb-5">
                <UserPlus size={20} className="text-[#635BFF]" />
              </div>
              <h1 className="font-display text-2xl font-bold text-[#0A2540]">{t("auth.registerTitle")}</h1>
              <p className="text-sm text-[#425466] mt-1">{t("auth.registerSubtitle")}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-[#425466] text-xs font-medium">{t("auth.fullName")}</Label>
                  <Input data-testid="register-name-input" value={form.full_name} onChange={set("full_name")}
                         className={inputCls} required />
                </div>
                <div>
                  <Label className="text-[#425466] text-xs font-medium">{t("auth.email")}</Label>
                  <Input data-testid="register-email-input" type="email" value={form.email} onChange={set("email")}
                         className={inputCls} required />
                </div>
                <div>
                  <Label className="text-[#425466] text-xs font-medium">{t("auth.phone")}</Label>
                  <Input data-testid="register-phone-input" value={form.phone} onChange={set("phone")}
                         className={inputCls} required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#425466] text-xs font-medium">{t("auth.password")}</Label>
                    <Input data-testid="register-password-input" type="password" value={form.password} onChange={set("password")}
                           className={inputCls} minLength={8} required />
                  </div>
                  <div>
                    <Label className="text-[#425466] text-xs font-medium">{t("auth.confirm")}</Label>
                    <Input data-testid="register-confirm-input" type="password" value={form.confirm} onChange={set("confirm")}
                           className={inputCls} minLength={8} required />
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    data-testid="register-cgu-checkbox"
                    checked={accept}
                    onCheckedChange={setAccept}
                    className="mt-0.5 border-[#CBD5E1] data-[state=checked]:bg-[#635BFF] data-[state=checked]:border-[#635BFF]"
                  />
                  <span className="text-xs text-[#425466] leading-relaxed">
                    {t("auth.acceptCgu")} —{" "}
                    <Link to="/legal/cgu" className="text-[#635BFF] hover:underline">CGU</Link> ·{" "}
                    <Link to="/legal/privacy" className="text-[#635BFF] hover:underline">RGPD</Link>
                  </span>
                </div>
                {error && (
                  <p data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
                <Button data-testid="register-submit-btn" type="submit" disabled={loading}
                        className="w-full bg-[#635BFF] hover:bg-[#0A2540] text-white h-11 font-semibold rounded-full transition-colors">
                  {loading ? t("common.loading") : t("auth.registerBtn")}
                </Button>
              </form>
              <p className="text-sm text-[#425466] mt-6 text-center">
                {t("auth.hasAccount")}{" "}
                <Link to="/login" className="text-[#635BFF] hover:underline font-semibold">
                  {t("auth.loginBtn")}
                </Link>
              </p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
